import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Clock, ShieldCheck } from 'lucide-react';
import { apiOrThrow } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';

interface MeResponse {
  id: string;
  username: string;
  fullName: string;
  role: string;
  employeeId: string | null;
  active: boolean;
  lastLoginAt: string | null;
}

export function DashboardPage() {
  const { user } = useAuth();

  // Smoke-test query: hits /auth/me to confirm JWT is valid + auto-attaches.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => apiOrThrow<MeResponse>('/auth/me'),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-charcoal">
          Welcome, {user?.fullName ?? 'there'}
        </h1>
        <p className="mt-1 text-sm text-brand-gray-mid">
          Phase 3 scaffold — feature pages ship in Phase 4.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-brand-red">
              <CalendarDays className="h-5 w-5" aria-hidden />
              <CardTitle className="text-base">Timesheet</CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-brand-gray-mid">
              Enter hours by day, submit for approval. Coming in Phase 4.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-brand-red">
              <Clock className="h-5 w-5" aria-hidden />
              <CardTitle className="text-base">Clock-in / out</CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-brand-gray-mid">
              Separate menu, GPS required at every event. Coming in Phase 4.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-brand-red">
              <ShieldCheck className="h-5 w-5" aria-hidden />
              <CardTitle className="text-base">Audit-ready</CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-brand-gray-mid">
              Every state change writes to <code>audit_logs</code> — survives user deletion.
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connection check</CardTitle>
        </CardHeader>
        <CardBody>
          {meQuery.isPending && (
            <p className="text-sm text-brand-gray-mid">Calling <code>/auth/me</code>…</p>
          )}
          {meQuery.isError && (
            <p className="text-sm text-brand-danger">
              Failed: {(meQuery.error as Error).message}
            </p>
          )}
          {meQuery.data && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-brand-gray-mid">Username</dt>
                <dd className="font-medium text-brand-charcoal">{meQuery.data.username}</dd>
              </div>
              <div>
                <dt className="text-brand-gray-mid">Role</dt>
                <dd className="font-medium text-brand-charcoal">{meQuery.data.role}</dd>
              </div>
              <div>
                <dt className="text-brand-gray-mid">Full name</dt>
                <dd className="font-medium text-brand-charcoal">{meQuery.data.fullName}</dd>
              </div>
              <div>
                <dt className="text-brand-gray-mid">Last login</dt>
                <dd className="font-medium text-brand-charcoal">
                  {meQuery.data.lastLoginAt
                    ? new Date(meQuery.data.lastLoginAt).toLocaleString()
                    : '—'}
                </dd>
              </div>
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
